/** @jsx jsx */
import { ClassNames, jsx } from '@emotion/react';
import { AfterPage, AfterPageProps } from '@sprinx/after-razzle';
import servicesData from 'api/servicesData';
import ApplicationCard from 'components/ApplicationCard';
import Breadcrumbs from 'components/Breadcrumbs';
import CommercialLine from 'components/CommercialLine';
import Container from 'components/Container';
import Page from 'components/Page';
import SectionIntroduction from 'components/SectionIntroduction';
import ProductImage from 'icons/ProductImage';
import MainLayout from 'layouts/MainLayout';
import ErrorPage from 'pages/ErrorPage';
import NotFoundPage from 'pages/NotFoundPage/NotFoundPage';
import LocalizationProvider from 'providers/LocalizationProvider';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { routeUrl } from 'routesBuilder';
import { ServicesData } from 'types/ServicesData';
import { SupportedLocale, SupportedRegion } from 'types/Supported';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ServicesPageProps extends AfterPageProps<ServicesPageRouteParams> {
  data?: ServicesData;
  locale: SupportedLocale;
  region: SupportedRegion;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ServicesPageRouteParams {
  locale: SupportedLocale;
  region: SupportedRegion;
}

const ServicesPage: AfterPage<ServicesPageRouteParams, ServicesPageProps> = ({ data, error, locale, region }) => {
  if (error) return <ErrorPage locale={locale} region={region} error={error} />;
  if (!data) return <NotFoundPage locale={locale} region={region} />;

  const { layout, applications } = data;

  return (
    <LocalizationProvider locale={locale} region={region}>
      <ClassNames>
        {({ theme }) => (
          <MainLayout {...layout} locale={locale}>
            <Page
              breadcrumbs={
                <Breadcrumbs>
                  <Link to={routeUrl('home', { locale, params: { locale: locale, region: region } })}>AppOn.cloud</Link>
                  <Link
                    to={routeUrl('services', { locale, params: { locale: locale, region: region } })}
                    className='active'
                  >
                    <FormattedMessage id={'navigation.services'} />
                  </Link>
                </Breadcrumbs>
              }
              pageTitle={data.title}
              css={{ width: '100%' }}
            >
              <SectionIntroduction>
                <div
                  css={{
                    '@media(max-width: 600px)': { paddingLeft: 15 },
                    ...theme.typography.h1,
                    margin: theme.spacing(2, 0),
                    textAlign: 'left',
                  }}
                >
                  <FormattedMessage id={'services.title'} />
                </div>
                <ProductImage
                  css={{
                    '@media(min-width: 600px)': { display: 'block', height: 400, top: -70 },
                    '@media(min-width: 960px)': {
                      right: -90,
                      top: -90,
                    },
                    '@media(min-width: 1180px)': {
                      height: 400,
                      width: 800,
                    },
                    bottom: 0,
                    display: 'none',
                    position: 'absolute',
                    right: 0,
                    width: '60%',
                    zIndex: -1,
                  }}
                />
              </SectionIntroduction>
              <div
                css={{
                  alignItems: 'center',
                  background: theme.palette.backgroundGradient,
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <CommercialLine />
                <Container maxWidth='lg' flexDirection='column'>
                  <div
                    css={{
                      alignSelf: 'center',
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      maxWidth: theme.breakpoints.values.lg,
                      padding: theme.spacing(1),
                      width: '100%',
                    }}
                  >
                    {(applications || []).map((app, idx) => (
                      <ApplicationCard
                        application={app}
                        key={idx.toString()}
                        path={routeUrl('servicesDetail', {
                          locale,
                          params: { id: app.path, locale: locale, region: region },
                        })}
                      />
                    ))}
                  </div>
                </Container>
              </div>
            </Page>
          </MainLayout>
        )}
      </ClassNames>
    </LocalizationProvider>
  );
};

ServicesPage.getInitialProps = async ({ match }) => {
  const { locale, region } = match.params;
  const data = await servicesData.get({ locale, region });

  return {
    data,
    locale,
    region,
  };
};

export default ServicesPage;
